import { normalizeText } from "../editorial/text.js";
import type { VisualBrief } from "../editorial/engine.js";

/**
 * Visual direction of a carousel (docs/VISUAL_DIRECTION_V1.md): what the
 * images must do for this subject, on top of the OPULANCIA signature that
 * never changes (dominant image, black foundation under the text, condensed
 * white titles, a few gold words, discreet brand at the top, swipe rhythm).
 *
 *   spectacle     Spectacle narratif — an immersive scene, the subject identifiable, tension or mystery
 *   evidence      Preuve / document — the photograph, archive or object itself, readable, restrained;
 *                 a reconstruction is never passed off as the real document
 *   before_after  Avant / après — two directly comparable states, same viewpoint, framing and scale
 *   scale         Échelle / explication — a size or an abstract idea made tangible by one simple comparison
 *
 * It is **not** the editorial family (families.ts): the family decides how the
 * story progresses (list, ranking, story, comparison) and the layout of each
 * slide; the direction decides what the pictures prove or show. A `story` can
 * be told with documents, a `ranking` can be about scale, a `comparison` can
 * be two archives. The family is only one weak signal among others here.
 *
 * The choice is explicit and correctable: a person sets it in the editorial
 * request (`visual_direction: { id, reason }`); otherwise it is **proposed**
 * from visible signals (the subject's words, the slides' structure, the
 * family), with the signals written down. When the signals do not separate
 * one direction clearly, the proposal is marked `to_validate` with its
 * alternatives: no classification is presented as reliable when it is not.
 *
 * Pure: the same inputs always give the same choice. It only changes the image
 * prompts; the renderer, the templates and the gates never read it.
 */
export const VISUAL_DIRECTIONS = ["spectacle", "evidence", "before_after", "scale"] as const;
export type VisualDirectionId = (typeof VISUAL_DIRECTIONS)[number];

export interface VisualDirectionInfo {
  id: VisualDirectionId;
  /** French label, shown to editors. */
  label: string;
  /** When the subject needs it. */
  need: string;
  /** Carousel-wide instruction, the same in every prompt of the carousel. */
  prompt: string;
}

export const DIRECTION_INFO: Record<VisualDirectionId, VisualDirectionInfo> = {
  spectacle: {
    id: "spectacle",
    label: "Spectacle narratif",
    need: "le sujet se raconte : une scène, un moment, une tension à faire vivre",
    prompt:
      "Direction « spectacle narratif » : une scène immersive, le sujet identifiable au premier coup d’œil, une tension ou un mystère lisible dans l’image (un moment précis, pas un simple décor). Selon le sujet : photographie, mise en scène photoréaliste, spectaculaire ou humoristique, nette et crédible, sans jamais passer pour la photo d’un événement réel ; pas de spectacle IA générique (pas d’explosion gratuite, de ciel épique ou d’effet de lumière sans rapport avec le récit).",
  },
  evidence: {
    id: "evidence",
    label: "Preuve / document",
    need: "le sujet repose sur une pièce à montrer : photographie, archive, document ou objet",
    prompt:
      "Direction « preuve / document » : l’image montre la pièce elle-même (photographie, archive, document ou objet), entière et reconnaissable d’un coup d’œil, cadrage frontal ou léger trois-quarts, traitement retenu, sans dramatisation. Une reconstitution n’est jamais présentée comme un document authentique : une image générée évoque la pièce sans l’imiter (aucun texte, tampon, signature, en-tête ni sceau lisible).",
  },
  before_after: {
    id: "before_after",
    label: "Avant / après",
    need: "le sujet est un changement : deux états du même sujet à comparer",
    prompt:
      "Direction « avant / après » : deux états directement comparables du même sujet. Même point de vue, même hauteur et même axe de caméra, même focale, même échelle et même cadrage d’un état à l’autre ; seule change la chose comparée (époque, état, taille). Lumière et saison aussi proches que possible, pour que la différence saute aux yeux.",
  },
  scale: {
    id: "scale",
    label: "Échelle / explication",
    need: "le sujet est une grandeur ou une idée abstraite à rendre tangible",
    prompt:
      "Direction « échelle / explication » : rendre la grandeur ou l’idée immédiatement tangible par une comparaison simple, le sujet à côté d’un repère familier (silhouette humaine, voiture, immeuble, main) dans le même plan et à la même distance. Perspective honnête : pas de grand-angle déformant ni de montage d’échelle trompeur. Un seul repère par image, lisible en une seconde ; ni infographie, ni flèche, ni chiffre dans l’image.",
  },
};

/** How a person sets the direction (editorial-request.json → visual_direction). */
export interface RequestedDirection {
  id: VisualDirectionId;
  /** Why this direction: kept in the briefs and the report. */
  reason: string;
}

export interface VisualDirectionChoice {
  id: VisualDirectionId;
  label: string;
  /** requested: set by a person; inferred: proposed from the signals below. */
  source: "requested" | "inferred";
  /**
   * chosen: a person set it. proposed: the signals point clearly to one direction (still a proposal, reviewed
   * with the images). to_validate: the signals are weak or split; a person must confirm or correct it.
   */
  status: "chosen" | "proposed" | "to_validate";
  reason: string;
  /** Inferred only: every signal counted, per direction (empty for a requested direction). */
  signals: Array<{ direction: VisualDirectionId; points: number; signal: string }>;
  scores: Record<VisualDirectionId, number>;
  /** Other directions worth considering (close scores); empty when the choice is clear or requested. */
  alternatives: VisualDirectionId[];
  /** Points to check in the briefs (e.g. before/after without a split slide, mixed realism). */
  notes: string[];
}

/** A slide as the direction sees it: the plan slide with its visual brief. */
export interface DirectionSlide {
  role: string;
  type: string;
  variant?: string;
  panels?: Array<{ label: string }>;
  figure?: { value: string; unit?: string };
  visual?: VisualBrief | null;
}

export interface DirectionInput {
  subject: string;
  /** Editorial family of the plan: one weak signal, never the direction itself. */
  family?: string | undefined;
  slides: DirectionSlide[];
  requested?: RequestedDirection;
}

/**
 * Subject words (normalised: lowercase, no accents) that say what the images
 * must do. A match counts 2 points, once per direction.
 */
const SUBJECT_WORDS: Record<VisualDirectionId, RegExp> = {
  spectacle:
    /\b(histoire|catastrophe|drame|tragedie|naufrage|explosion|incendie|disparu|disparue|disparaitre|disparition|detruit|detruite|mystere|mysterieux|mysterieuse|legende|secret|affaire|jour ou|a failli|survivants?|crash|evasion|braquage)\b/g,
  evidence:
    /\b(documents?|archives?|preuves?|photographies?|lettres?|brevets?|contrats?|conventions?|actes?|carnets?|manuscrits?|factures?|telegrammes?|papiers?|authentiques?|originale?s?|faux|dossiers?|objets?)\b/g,
  before_after:
    /\b(avant|apres|evolution|a change|a grandi|grandir|grandit|hier|aujourd hui|transformation|restauration|restaure|restauree|vs|versus|autrefois|devenu|devenue|a l epoque)\b/g,
  scale:
    /\b(taille|echelle|geant|geante|gigantesque|immense|minuscule|hauteur|plus hauts?|plus hautes?|plus grands?|plus grandes?|plus petits?|plus petites?|mesurait|mesure|metres|kilometres|tonnes|poids|fois plus|combien|equivaut|volume|surface|longueur|vitesse|chiffres|millions?|milliards?|prix|vendue?|vend|vendent|coute|coutent)\b/g,
};

/** Family → the direction it most often calls for. One point: a hint, not a rule. */
const FAMILY_HINT: Record<string, VisualDirectionId | undefined> = {
  story: "spectacle",
  comparison: "before_after",
  ranking: "scale",
  list: undefined,
};

/** Units of a physical magnitude (figures on the slides). */
const SIZE_UNIT = /^(m|km|cm|mm|t|kg|g|tonnes?|metres?|kilometres?|km h|m2|m²|ha|l|litres?|m3|m³)$/;

/** Words in the visual briefs that already ask for a scale reference or a document. */
const BRIEF_SCALE = /\b(echelle|a cote de|silhouettes? humaines?|pour comparer)\b/;
const BRIEF_EVIDENCE = /\b(document|archive|plans? techniques?|brevet|convention|lettre|manuscrit|registre|tirage)\b/;

/** Order used when two directions tie: the more demanding first (proof needs sources, comparison needs matching views). */
const TIE_ORDER: VisualDirectionId[] = ["evidence", "before_after", "scale", "spectacle"];

const emptyScores = (): Record<VisualDirectionId, number> => ({ spectacle: 0, evidence: 0, before_after: 0, scale: 0 });

/** Points to check whatever the direction: mixed realism, a before/after without two panels. */
function notesFor(id: VisualDirectionId, slides: DirectionSlide[]): string[] {
  const notes: string[] = [];
  const withImage = slides.filter((s) => s.visual);
  const realisms = new Map<string, number>();
  for (const slide of withImage) realisms.set(slide.visual!.realism, (realisms.get(slide.visual!.realism) ?? 0) + 1);
  if (realisms.size > 1) {
    notes.push(
      `réalismes mélangés (${[...realisms].map(([r, n]) => `${r} ×${n}`).join(", ")}) : vérifier que l’écart est éditorial (ex. une archive comme preuve) et garder la même lumière sur toute la série`,
    );
  }
  if (id === "before_after" && !slides.some((s) => s.variant === "split" && s.panels?.length === 2)) {
    notes.push("avant / après sans slide à deux panneaux : la comparaison se fera d’une slide à l’autre, gardez exactement le même point de vue");
  }
  if (id === "evidence" && withImage.some((s) => s.visual!.realism === "ai_photoreal" || s.visual!.realism === "illustration")) {
    notes.push("preuve / document avec des images générées : ce sont des reconstitutions, mention « Reconstitution IA » ; la pièce réelle (source et droits) reste préférable");
  }
  return notes;
}

/**
 * The direction of a carousel: the requested one, else a proposal from the
 * signals, marked `to_validate` when the signals are weak or split.
 */
export function selectVisualDirection(input: DirectionInput): VisualDirectionChoice {
  const scores = emptyScores();
  if (input.requested) {
    const { id, reason } = input.requested;
    return {
      id,
      label: DIRECTION_INFO[id].label,
      source: "requested",
      status: "chosen",
      reason,
      signals: [],
      scores,
      alternatives: [],
      notes: notesFor(id, input.slides),
    };
  }

  const signals: VisualDirectionChoice["signals"] = [];
  const add = (direction: VisualDirectionId, points: number, signal: string) => {
    scores[direction] += points;
    signals.push({ direction, points, signal });
  };

  // 1. the subject's own words (what the editor announces)
  const subject = normalizeText(input.subject);
  const fromSubject = new Set<VisualDirectionId>();
  for (const id of VISUAL_DIRECTIONS) {
    const words = [...new Set(subject.match(SUBJECT_WORDS[id]) ?? [])];
    if (words.length) {
      add(id, 2, `sujet : « ${words.join(" », « ")} »`);
      fromSubject.add(id);
    }
  }

  // 2. the structure of the slides
  const slides = input.slides.filter((s) => s.visual);
  const splits = input.slides.filter((s) => s.variant === "split" && s.panels?.length === 2);
  if (splits.length) add("before_after", 2, `${splits.length} slide(s) à deux panneaux (${splits.map((s) => s.panels!.map((p) => p.label).join(" / ")).join(" ; ")})`);
  const archives = slides.filter((s) => s.visual!.realism === "archive").length;
  if (archives) add("evidence", archives * 2 >= slides.length ? 2 : 1, `${archives}/${slides.length} image(s) d’archive demandée(s)`);
  const cards = input.slides.filter((s) => s.variant === "card").length;
  if (cards) add("evidence", 1, `${cards} image(s) à montrer entière(s) (card)`);
  const sized = input.slides.filter((s) => s.figure?.unit && SIZE_UNIT.test(normalizeText(s.figure.unit)));
  if (sized.length >= 2) add("scale", 1, `${sized.length} grandeurs mesurées (${sized.map((s) => `${s.figure!.value} ${s.figure!.unit}`).join(", ")})`);
  const briefText = (s: DirectionSlide) => normalizeText([s.visual!.shows, s.visual!.composition, s.visual!.why].join(" "));
  const scaleBriefs = slides.filter((s) => BRIEF_SCALE.test(briefText(s))).length;
  if (scaleBriefs) add("scale", 1, `${scaleBriefs} brief(s) visuel(s) demandant un repère d’échelle`);
  const evidenceBriefs = slides.filter((s) => BRIEF_EVIDENCE.test(normalizeText(s.visual!.shows))).length;
  if (evidenceBriefs) add("evidence", 1, `${evidenceBriefs} brief(s) visuel(s) montrant un document ou une archive`);

  // 3. the family: a hint only
  const hint = input.family ? FAMILY_HINT[input.family] : undefined;
  if (hint) add(hint, 1, `famille « ${input.family} » (indice faible)`);

  // ranking: highest score; ties go to the direction the subject names, then to the more demanding one
  const ranked = [...VISUAL_DIRECTIONS].sort(
    (a, b) => scores[b] - scores[a] || Number(fromSubject.has(b)) - Number(fromSubject.has(a)) || TIE_ORDER.indexOf(a) - TIE_ORDER.indexOf(b),
  );
  const top = ranked[0]!;
  const second = ranked[1]!;
  const why = signals.filter((s) => s.direction === top).map((s) => s.signal);
  if (scores[top] === 0) {
    return {
      id: "spectacle",
      label: DIRECTION_INFO.spectacle.label,
      source: "inferred",
      status: "to_validate",
      reason: "aucun signal dans le sujet ni dans les slides : proposition par défaut (spectacle narratif), à confirmer",
      signals,
      scores,
      alternatives: ["evidence", "before_after", "scale"],
      notes: notesFor("spectacle", input.slides),
    };
  }
  const clear = scores[top] >= 2 && scores[top] - scores[second] >= 2;
  const alternatives = clear ? [] : ranked.slice(1).filter((id) => scores[id] > 0 && scores[top] - scores[id] <= 1);
  return {
    id: top,
    label: DIRECTION_INFO[top].label,
    source: "inferred",
    status: clear ? "proposed" : "to_validate",
    reason: clear
      ? `${why.join(" ; ")} (${scores[top]} points, suivante : ${DIRECTION_INFO[second].label.toLowerCase()} à ${scores[second]})`
      : `${why.join(" ; ")} (${scores[top]} points) contre ${alternatives.map((id) => `${DIRECTION_INFO[id].label.toLowerCase()} (${scores[id]})`).join(", ") || "des signaux trop faibles pour trancher"}`,
    signals,
    scores,
    alternatives,
    notes: notesFor(top, input.slides),
  };
}

// ---- prompt lines ------------------------------------------------------------------------

/**
 * What the direction asks of one slide: `prompt` goes into the GPT Image prompt (written for the model);
 * `note`, when present, is for the person producing the image (image-briefs.md only).
 */
export function slideDirection(
  id: VisualDirectionId,
  slide: { role: string; composition: string; panel?: 0 | 1; panel_label?: string; figure?: { value: string; unit?: string }; realism: VisualBrief["realism"] },
): { prompt: string; note?: string } {
  switch (id) {
    case "spectacle":
      if (slide.role === "hook") return { prompt: "Pour cette slide : la scène qui pose la question du carrousel sans y répondre ; le sujet reconnaissable, la tension visible." };
      if (slide.role === "reveal" || slide.role === "payoff") return { prompt: "Pour cette slide : le moment de bascule du récit, au plus près du sujet, dans le même monde que la couverture." };
      return { prompt: "Pour cette slide : une étape du récit, un moment précis et concret, dans le même monde que la couverture." };
    case "evidence":
      return slide.realism === "photo" || slide.realism === "archive"
        ? {
            prompt: "Pour cette slide : la pièce elle-même, entière et nette, sous une lumière neutre et douce ; une image générée ici est une reconstitution qui évoque la pièce, sans en reproduire le texte, le tampon ni la signature.",
            note: "priorité à la pièce réelle (photo, archive) avec sa source et ses droits dans images.json ; le prompt ne sert qu’à défaut, pour une reconstitution signalée « Reconstitution IA », jamais présentée comme l’original",
          }
        : {
            prompt: "Pour cette slide : une reconstitution assumée, qui évoque la pièce ou la scène sans se faire passer pour un document authentique.",
            note: "image générée : mention « Reconstitution IA » sur la slide ; ne jamais la présenter comme une archive ou un document",
          };
    case "before_after":
      if (slide.composition === "split") {
        return {
          prompt: `Pour ce panneau (${slide.panel === 0 ? "gauche" : "droit"}, « ${slide.panel_label ?? ""} ») : un seul état ; il doit se superposer à l’autre panneau, sujet à la même place et à la même taille dans le cadre, même axe, même lumière.`,
          note: "produire les deux panneaux ensemble et les comparer côte à côte avant de les déposer ; si les points de vue diffèrent, régénérer plutôt que recadrer",
        };
      }
      return { prompt: "Pour cette slide : le point de vue de référence de la comparaison, pour que le lecteur retrouve le sujet à l’identique d’une slide à l’autre." };
    case "scale": {
      const value = slide.figure ? `${slide.figure.value}${slide.figure.unit ? ` ${slide.figure.unit}` : ""}` : null;
      // the composition's framing wins: the reference stays inside the subject's zone
      const zone =
        slide.composition === "impact"
          ? " Le repère reste collé au sujet, dans la zone qui sera agrandie."
          : slide.composition === "offset"
            ? " Le repère est du même côté que le sujet, jamais dans la moitié réservée au texte."
            : slide.composition === "split"
              ? " Même repère, à la même taille, dans les deux panneaux."
              : "";
      return value
        ? {
            prompt: `Pour cette slide : rendre visible la grandeur « ${value} » par le repère d’échelle (la valeur est affichée par la mise en page, elle n’est jamais écrite dans l’image).${zone}`,
            note: "vérifier que le repère est à la bonne proportion : une échelle fausse est une affirmation fausse",
          }
        : { prompt: `Pour cette slide : le sujet et son repère d’échelle dans le même plan, la comparaison comprise sans légende.${zone}` };
    }
  }
}

/** Constant OPULANCIA signature lines, added to every prompt that carries a direction. */
export const SIGNATURE_LINE =
  "Signature OPULANCIA : l’image doit être forte sans aucun texte ; aucun titre, logo, typographie ni cadre ajouté (la marque OPULANCIA en haut, le titre blanc condensé, les mots dorés et la fondation noire sont posés par la mise en page) ; le sujet ne touche pas le bord supérieur, où la marque est posée.";

export function seriesLine(realisms: string[]): string {
  const distinct = [...new Set(realisms)];
  return distinct.length === 1
    ? `Série : même niveau de réalisme et même traitement de lumière que les autres images du carrousel.`
    : `Série : même traitement de lumière et même profondeur que les autres images du carrousel ; l’écart de réalisme (${distinct.join(", ")}) n’est permis que pour une raison éditoriale.`;
}

/** How a person corrects the direction, written in the briefs. */
export const CORRECTION_HELP =
  'Pour corriger : dans `editorial-request.json`, ajoutez `"visual_direction": { "id": "spectacle" | "evidence" | "before_after" | "scale", "reason": "…" }` puis relancez `--briefs`. Le choix humain remplace la proposition et reste tracé.';
